import CreateTaskTags from "@/components/CreateTaskTags/createTaskTags.component";
import CreateTimerDialog from "@/components/CreateTimer/createTimerDialog.component";
import CustomLoader from "@/components/CustomLoader/customLoader.component";
import MultiPicker from "@/components/PersonPickerDropdown/multiPicker";
import ToggleTimer from "@/components/SingleLineTimer/toggleTimer.component";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { useUser } from "@/hooks/AppContext/user.hook";
import { AppContext } from "@/services/AppContext/appContext";
import { useTheme } from "@/shadcn/components/theme-provider";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { NewTaskTag, OrganizedTimersEvent, TagCategory } from "@/types";
import {
  copyToClipboard,
  formatElapsedTime,
  getNewTaskTagByID,
} from "@/utils/utils";
import { Braces, ListFilter, Plus } from "lucide-react";
import React, { useContext, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const AllTimersView: React.FC = () => {
  const { theme } = useTheme();
  const controllerRef = useRef(new AbortController());
  const {
    user,
    userTags,
    pageTimers,
    pageLoading,
    fetchPageTimers,
    toggleFetchAll,
    fetchEveryoneTimers,
    allAppUsers,
  } = useContext(AppContext);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const dev = import.meta.env.VITE_ENVIRONMENT === "development";
  const { getUserFullNameByID } = useUser();
  const isNotSmall = useMediaQuery("sm");

  // Filters
  const [assignedTo, setAssignedTo] = useState<number[]>([]);
  const multiAssigneeRef = useRef<HTMLDivElement | null>(null);

  const [tagData, setTagData] = useState<NewTaskTag[]>([]);
  const [timerName, setTimerName] = useState<string>("");

  const handleAssigneeChange = (
    assigneeID: number,
    setAssigneeState: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    setAssigneeState((prev) => {
      if (!prev) return [];

      const assigneeExists = prev.includes(assigneeID);

      const updatedAssignees = assigneeExists
        ? prev.filter((assignee) => assignee !== assigneeID)
        : [...prev, assigneeID];

      return updatedAssignees;
    });
  };

  const handleTagChange = (tag: NewTaskTag) => {
    console.log("New Tag: ", tag);

    setTagData((prev) => {
      if (!prev) return [];

      const tagExists = prev.some(
        (t) => t.tag === tag.tag && t.category_id === tag.category_id
      );

      const updatedTags = tagExists
        ? prev.filter(
            (t) => !(t.tag === tag.tag && t.category_id === tag.category_id)
          )
        : [...prev, tag];

      console.log("Updated Tags: ", updatedTags);
      return updatedTags;
    });
  };

  const handleTimerNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTimerName(event.target.value);
  };

  const clearFilters = () => {
    setAssignedTo([]);
    setTagData([]);
    setTimerName("");
  };

  // Apply filters
  const applyFilters = (
    timers: OrganizedTimersEvent[],
    filters: {
      assignedTo?: number[];
      tagData?: NewTaskTag[];
      timerName?: string;
    },
    userTags: TagCategory[]
  ): OrganizedTimersEvent[] => {
    return timers.filter((timer) => {
      // Filter by assignee
      const passesAssigneeFilter =
        !filters.assignedTo?.length ||
        filters.assignedTo.includes(timer.timer.user_id);

      // Filter by tags
      const passesTagFilter =
        !filters.tagData?.length ||
        timer.timer.tags?.some((tag) => {
          const tagName = getNewTaskTagByID(tag, userTags)?.tag;
          return (
            filters.tagData?.some((filterTag) => filterTag.tag === tagName) ??
            false
          );
        });

      // Filter by timer name
      const passesTimerNameFilter =
        !filters.timerName ||
        timer.timer.name
          .toLowerCase()
          .includes(filters.timerName.toLowerCase());

      return passesAssigneeFilter && passesTagFilter && passesTimerNameFilter;
    });
  };

  // Filtered Timers
  const filteredTimers = useMemo(() => {
    if (!pageTimers) return undefined;

    const filters = { assignedTo, tagData, timerName };

    console.log("timerName", timerName);
    const result = {
      today: applyFilters(pageTimers.today, filters, userTags),
      thisWeek: applyFilters(pageTimers.thisWeek, filters, userTags),
      older: pageTimers.older.map((olderTimerObject) => {
        console.log("olderTimerObject", olderTimerObject);
        return {
          month: olderTimerObject.month,
          timers: applyFilters(olderTimerObject.timers, filters, userTags),
        };
      }),
      noEvents: applyFilters(pageTimers.noEvents, filters, userTags),
    };

    return result;
  }, [pageTimers, assignedTo, tagData, timerName, userTags]);

  const handleToggleTimers = () => {
    toggleFetchAll();
  };

  const handleRefreshTimers = () => {
    fetchPageTimers(controllerRef.current.signal, fetchEveryoneTimers);
  };

  function handleCopyTimersToClipboard(period: "today" | "week") {
    console.time("handleCopyTimersToClipboard");
    try {
      if (!filteredTimers) return;
      const periodTimers =
        period === "today"
          ? filteredTimers.today
          : [
              // Flatten the arrays
              ...filteredTimers.today,
              ...filteredTimers.thisWeek,
            ];

      const adjustedTimers = prepareTimersForClipboard(periodTimers);

      copyToClipboard(
        "Timers",
        JSON.stringify(adjustedTimers, null, 2).replace(/\\"/g, "")
      );
    } catch (error) {
      console.error("Error copying timers to clipboard", error);
      toast.error("Error copying timers to clipboard", {
        description: "Please try again later.",
      });
    }
    console.timeEnd("handleCopyTimersToClipboard");
  }

  function prepareTimersForClipboard(timers: OrganizedTimersEvent[]) {
    return timers.map((timer) => {
      return {
        event_start: timer.event_start,
        event_end: timer.event_end || null,
        timer: {
          id: timer.timer.id,
          total_time: timer.timer.totalTime
            ? formatElapsedTime(timer.timer.totalTime)
            : null,
          name: timer.timer.name,
          task: {
            id: timer.timer.task_id,
            name: timer.timer.task_name,
            assignee: {
              id: timer.timer.user_id,
              name: getUserFullNameByID(timer.timer.user_id),
            },
            tags: timer.timer.tags
              ? timer.timer.tags.map((tag) => {
                  const foundTag = getNewTaskTagByID(tag, userTags);
                  return foundTag
                    ? {
                        name: foundTag.tag,
                        category: userTags.find(
                          (t) => t.id === foundTag.category_id
                        )?.category,
                      }
                    : undefined;
                })
              : [],
          },
        },
      };
    });
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="grid grid-cols-3 items-center px-4 pt-3 w-full">
        <h1 className="text-3xl font-bold text-center text-primary md:col-start-2">
          Timers
        </h1>
        <div className="flex justify-end col-start-3">
          <CreateTimerDialog>
            <Button
              variant={"default"}
              className={`rounded-full items-center justify-center `}
            >
              Create Timer <Plus className="h-5 ml-1" />
            </Button>
          </CreateTimerDialog>
        </div>
      </div>
      <div className="flex-grow m-4 mr-0 pr-4 overflow-y-auto">
        {pageLoading ? (
          <CustomLoader />
        ) : loadingError ? (
          loadingError
        ) : !filteredTimers ? (
          "No timers found"
        ) : (
          <>
            {/* Filters + Buttons */}
            <div className="w-full font-medium px-2 mb-5 flex lg:justify-between lg:items-center overflow-x-hidden flex-col lg:flex-row gap-2">
              <div className="flex gap-2 items-center pt-1">
                <div>
                  <ListFilter className="w-5 h-5" />
                </div>
                <Input
                  type="text"
                  placeholder="Filter by Timer Name"
                  value={timerName}
                  onChange={handleTimerNameChange}
                  className=""
                  maxLength={255}
                />

                {fetchEveryoneTimers &&
                  (dev ||
                    user.role_name === "project_manager" ||
                    user.role_name === "super_admin") && (
                    <MultiPicker
                      assignees={assignedTo}
                      handleFieldAssignees={(id) =>
                        handleAssigneeChange(id, setAssignedTo)
                      }
                      multiAssigneeRef={multiAssigneeRef}
                      type="Assignee"
                      side={isNotSmall ? "bottom" : undefined}
                      align={isNotSmall ? "start" : undefined}
                    />
                  )}
                <CreateTaskTags
                  id="edit-tags-search"
                  taskTags={tagData}
                  handleFieldTags={handleTagChange}
                  side={isNotSmall ? "right" : undefined}
                  align={isNotSmall ? "start" : undefined}
                  createTagAllowed={false}
                />
              </div>
              <div className="flex gap-2 items-center pt-1">
                <Button
                  variant={"outline"}
                  className={`items-center justify-center `}
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
                {/* Fetch Timers Button */}
                <Button
                  variant={"outline"}
                  className={`items-center justify-center `}
                  onClick={handleRefreshTimers}
                >
                  Refresh
                </Button>
                {(dev ||
                  user.role_name === "project_manager" ||
                  user.role_name === "super_admin") && (
                  <Button
                    variant={"outline"}
                    className={`items-center justify-center`}
                    onClick={handleToggleTimers}
                  >
                    {fetchEveryoneTimers ? "Show My Timers" : "Show All Timers"}
                  </Button>
                )}
                <Button
                  variant={"outline"}
                  className={`items-center justify-center hidden md:flex`}
                  onClick={handleCopyTimersToClipboard.bind(null, "today")}
                  disabled={!filteredTimers}
                >
                  <Braces />
                  Copy Today's Timers
                </Button>
                <Button
                  variant={"outline"}
                  className={`items-center justify-center hidden md:flex`}
                  onClick={handleCopyTimersToClipboard.bind(null, "week")}
                  disabled={!filteredTimers}
                >
                  <Braces />
                  Copy Week's Timers
                </Button>
              </div>
            </div>
            <div className="hidden md:grid grid-cols-12 items-center font-medium">
              <div className="col-span-2 px-1">Timer</div>
              <div className="col-span-2 px-2">Task</div>
              <div className="col-span-2 px-2">Assignee</div>
              <div className="col-span-2 px-2">Start Time</div>
              <div className="col-span-2 px-2">End Time</div>
              <div className="col-span-1 px-2">Tags</div>
              <div className="col-span-1 text-right px-2">Time</div>
            </div>
            <ToggleTimer type="Today" timers={filteredTimers.today} />
            <ToggleTimer type="This Week" timers={filteredTimers.thisWeek} />

            {filteredTimers.older.map((olderTimerObject, index) => {
              // console.log("olderTimerObject", olderTimerObject);
              return (
                <ToggleTimer
                  key={olderTimerObject.month + index}
                  type={olderTimerObject.month}
                  timers={olderTimerObject.timers}
                />
              );
            })}

            <ToggleTimer type="No Events" timers={filteredTimers.noEvents} />
          </>
        )}
      </div>
    </div>
  );
};

export default AllTimersView;
